/**
 * Authentication Context Provider.
 * Manages user authentication state across the application.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, getToken, getUser, getRefreshToken, removeToken, removeRefreshToken, removeUser, setToken, setRefreshToken, setUser as setUserLocalStorage, isApiError } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

interface User {
  id: string;
  email: string;
  full_name: string;
  is_platform_admin: boolean;
  agency_id: string | null;
  is_active: boolean;
  email_verified: boolean;
  created_at?: string;
  updated_at?: string;
}

interface Agency {
  id: string;
  name: string;
  slug: string;
  created_at: string;
}

interface JwtPayload {
  sub: string;
  agency_id?: string;
  role?: string;
  roles?: string[] | string;  // Legacy tokens (backward compat)
  is_platform_admin?: boolean;  // New tokens
  exp?: number;  // Expiry timestamp (standard JWT claim, seconds since epoch)
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Parse JWT payload to extract user claims.
 * JWT format: header.payload.signature
 */
export const parseJwtPayload = (token: string): JwtPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/**
 * Check if a JWT access token is expired (with a small safety window).
 * Returns true if the token is missing, malformed, or its `exp` claim is
 * within `SKEW_SECONDS` of the current time.
 *
 * Spec 2026-06-30-frontend-token-refresh: used by AuthContext on app load
 * to decide whether to attempt a refresh-token rotation up front.
 */
export const isTokenExpired = (token: string | null, skewSeconds = 30): boolean => {
  if (!token) return true;
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return true;
  }
  const expSeconds = payload.exp;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return expSeconds - skewSeconds <= nowSeconds;
};

/**
 * Extract roles array from JWT payload.
 * New tokens use is_platform_admin boolean.
 * Legacy tokens use roles array — we extract platform_admin from it.
 */
const extractRoles = (payload: JwtPayload): string[] => {
  // New token format: is_platform_admin flag
  if (payload.is_platform_admin === true) {
    return ['platform_admin'];
  }

  // Legacy token format: roles array
  if (Array.isArray(payload.roles)) {
    return payload.roles.includes('platform_admin') ? ['platform_admin'] : [];
  }
  if (typeof payload.roles === 'string') {
    return payload.roles === 'platform_admin' ? ['platform_admin'] : [];
  }

  return [];
};

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  roles: string[];
  isPlatformAdmin: boolean;
  agencies: Agency[];
  currentAgency: Agency | null;
  setCurrentAgency: (agency: Agency | null) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (
    fullName: string,
    email: string,
    password: string,
    confirmPassword: string,
    acceptTerms: boolean
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  setRoles: (roles: string[]) => void;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [currentAgency, setCurrentAgencyState] = useState<Agency | null>(null);
  const navigate = useNavigate();

  // Computed value: is user a platform admin?
  const isPlatformAdmin = roles.includes('platform_admin');

  // Agency selection with localStorage persistence
  const setCurrentAgency = useCallback((agency: Agency | null) => {
    setCurrentAgencyState(agency);
    if (agency) {
      localStorage.setItem('currentAgencyId', agency.id);
    } else {
      localStorage.removeItem('currentAgencyId');
    }
  }, []);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      let token = getToken();
      const savedUser = getUser() as User | null;
      // Spec 2026-06-30-frontend-token-refresh: if we have a refresh_token
      // but the access_token is missing or expired, try to refresh up front
      // so the user lands on the dashboard already authenticated (30-day
      // session) rather than being kicked to /login on the first API call.
      // Inlined rather than calling the refreshToken callback to avoid a
      // temporal-dead-zone dependency on its useCallback definition below.
      const refresh = getRefreshToken();
      if ((!token || isTokenExpired(token)) && refresh) {
        try {
          const refreshed = await authApi.refreshToken(refresh);
          if (refreshed.access_token) {
            setToken(refreshed.access_token);
          }
          const newRefresh = (refreshed as { refresh_token?: string }).refresh_token;
          if (newRefresh) {
            setRefreshToken(newRefresh);
          }
          if (refreshed.user) {
            setUserLocalStorage(refreshed.user as User);
          }
          token = refreshed.access_token || getToken();
        } catch {
          // Refresh failed — fall through to the original validation path,
          // which will eventually clear state and require re-login.
        }
      }

      if (token && savedUser) {
        // Validate token with backend before setting auth state
        try {
          const validatedUser = await authApi.getMe();
          setUser(validatedUser as User);
          setUserLocalStorage(validatedUser as User);
          // Parse roles from JWT token
          const payload = parseJwtPayload(token);
          if (payload) {
            setRoles(extractRoles(payload));
          }
          // Fetch user's agencies
          try {
            const response = await fetch('/api/v1/agencies', {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
              const data = await response.json();
              const userAgencies: Agency[] = data.data?.agencies || data.agencies || [];
              setAgencies(userAgencies);
              // Restore previously selected agency from localStorage
              const savedAgencyId = localStorage.getItem('currentAgencyId');
              if (savedAgencyId && userAgencies.length > 0) {
                const saved = userAgencies.find((a) => a.id === savedAgencyId);
                if (saved) {
                  setCurrentAgencyState(saved);
                } else if (userAgencies.length > 0) {
                  setCurrentAgencyState(userAgencies[0]);
                }
              } else if (userAgencies.length > 0) {
                setCurrentAgencyState(userAgencies[0]);
              }
            }
          } catch {
            // Non-blocking: agencies fetch failure shouldn't prevent login
          }
        } catch (error) {
          // Token is invalid or expired - clear auth state
          console.warn('[Auth] Token validation failed, clearing auth state', error);
          removeToken();
          removeRefreshToken();
          removeUser();
          setUser(null);
          setRoles([]);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Login function
  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password });

      // Driver #10 (TOTP MFA): if the user has MFA enabled, the backend
      // returns { requires_totp: true, challenge_token: "..." } instead
      // of access tokens. We re-throw as a structured error so the
      // LoginForm can route the user to the TOTP verify UI. The full
      // challenge-response UX (verify page, backup-code entry, etc.)
      // is implemented in a follow-on task; this guard prevents the
      // login flow from navigating to /dashboard with no token set.
      if ((response as { requires_totp?: boolean }).requires_totp) {
        const mfaError = new Error('MFA challenge required') as Error & {
          requires_totp?: boolean;
          challenge_token?: string;
        };
        mfaError.requires_totp = true;
        mfaError.challenge_token = (response as { challenge_token?: string }).challenge_token;
        throw mfaError;
      }

      if (response.user) {
        setUser(response.user as User); // React state
        setUserLocalStorage(response.user as User); // localStorage
      }
      if (response.access_token) {
        setToken(response.access_token);
        // Parse roles from JWT token
        const payload = parseJwtPayload(response.access_token);
        if (payload) {
          setRoles(extractRoles(payload));
        }
      }
      // Spec 2026-06-30-frontend-token-refresh: store refresh_token so the
      // 401 interceptor can extend the session past 30 minutes. Backward
      // compatible — older API versions that omit refresh_token simply fall
      // back to the legacy 30-minute access-token-only session.
      const refreshToken = (response as { refresh_token?: string }).refresh_token;
      if (refreshToken) {
        setRefreshToken(refreshToken);
      }
      // Fetch user's agencies after login
      try {
        const token = response.access_token || getToken();
        if (token) {
          const agenciesResponse = await fetch('/api/v1/agencies', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (agenciesResponse.ok) {
            const data = await agenciesResponse.json();
            const userAgencies: Agency[] = data.data?.agencies || data.agencies || [];
            setAgencies(userAgencies);
            if (userAgencies.length > 0) {
              setCurrentAgencyState(userAgencies[0]);
            }
          }
        }
      } catch {
        // Non-blocking
      }
      navigate('/dashboard');
    } catch (error) {
      // Re-throw ApiError for components to handle
      if (isApiError(error)) {
        throw error;
      }
      // Wrap unknown errors
      throw new Error('Login failed. Please try again.');
    }
  }, [navigate]);

  // Register function
  const register = useCallback(async (
    fullName: string,
    email: string,
    password: string,
    confirmPassword: string,
    acceptTerms: boolean
  ) => {
    try {
      const response = await authApi.register({
        full_name: fullName,
        email,
        password,
        confirm_password: confirmPassword,
        accept_terms: acceptTerms,
      });

      // Store JWT token
      if (response.access_token) {
        setToken(response.access_token);
      }

      // Store refresh_token (spec 2026-06-30-frontend-token-refresh).
      // Backward compat: missing field → no refresh available, 30-min session.
      const registerRefresh = (response as { refresh_token?: string }).refresh_token;
      if (registerRefresh) {
        setRefreshToken(registerRefresh);
      }

      // Store user - use response.user or parse from JWT if null
      let userToStore = response.user as User | null;

      if (!userToStore && response.access_token) {
        // If user is null, parse user from JWT payload
        const payload = parseJwtPayload(response.access_token);
        if (payload) {
          userToStore = {
            id: payload.sub,
            email: email, // Email is not in JWT payload, use from input
            full_name: '', // Not available in JWT
            is_platform_admin: payload.is_platform_admin || false,
            agency_id: payload.agency_id || null,
            is_active: true,
            email_verified: false,
          };
        }
      }

      if (userToStore) {
        setUser(userToStore); // React state
        setUserLocalStorage(userToStore); // localStorage
      }

      // Parse and set roles from JWT
      if (response.access_token) {
        const payload = parseJwtPayload(response.access_token);
        if (payload) {
          setRoles(extractRoles(payload));
        }
      }

      // Fetch user's agencies after registration
      try {
        const token = response.access_token || getToken();
        if (token) {
          const agenciesResponse = await fetch('/api/v1/agencies', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (agenciesResponse.ok) {
            const data = await agenciesResponse.json();
            const userAgencies: Agency[] = data.data?.agencies || data.agencies || [];
            setAgencies(userAgencies);
            if (userAgencies.length > 0) {
              setCurrentAgencyState(userAgencies[0]);
            }
          }
        }
      } catch {
        // Non-blocking
      }

      navigate('/dashboard');
    } catch (error) {
      // Re-throw ApiError for components to handle
      if (isApiError(error)) {
        throw error;
      }
      // Wrap unknown errors
      throw new Error('Registration failed. Please try again.');
    }
  }, [navigate]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors during logout
    } finally {
      setUser(null);
      setRoles([]);
      removeToken();
      removeRefreshToken();
      removeUser();
      navigate('/login');
    }
  }, [navigate]);

  // Refresh token function
  // Spec 2026-06-30-frontend-token-refresh: use the stored refresh token
  // (NOT the access token, which is the bug the original implementation
  // had). Stores the rotated access + refresh token pair on success.
  const refreshToken = useCallback(async () => {
    const refresh = getRefreshToken();
    if (!refresh) {
      throw new Error('No refresh token available');
    }

    const response = await authApi.refreshToken(refresh);
    if (response.user) {
      setUser(response.user as User); // React state
      setUserLocalStorage(response.user as User); // localStorage
    }
    if (response.access_token) {
      setToken(response.access_token);
      // Parse and update roles from new token
      const payload = parseJwtPayload(response.access_token);
      if (payload) {
        setRoles(extractRoles(payload));
      }
    }
    // Store the rotated refresh token (backward compat: older responses
    // may not include a new refresh_token).
    const newRefresh = (response as { refresh_token?: string }).refresh_token;
    if (newRefresh) {
      setRefreshToken(newRefresh);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    roles,
    isPlatformAdmin,
    agencies,
    currentAgency,
    setCurrentAgency,
    login,
    register,
    logout,
    refreshToken,
    setRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ============================================================================
// Protected Route Component
// ============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = redirectTo;
    return null;
  }

  return <>{children}</>;
};
